import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { rectCollision } from './forces';
import { dragHook, useForceSimulation, useZoom } from './forceUtils';
import pick from 'lodash-es/pick';

/**
 * Renders a simple node-link diagram.
 *
 * Expects: {
 *   nodes: [],
 *   links: [],
 * }
 *
 * @param data
 * @param dimensions
 * @param onSelect
 * @param multiselect
 * @param selected
 * @returns {*}
 * @constructor
 */
export const TypographWidget = ({ data, dimensions, onSelect, multiselect = false, selected = [] }) => {
	const opacity = { stroke: 0.4, fill: 0.3 };

	const { nodes, links } = data;
	const svgRef = useRef(null);
	const centroidGroupRef = useRef(null);
	const termGroupRef = useRef(null);
	const containerRef = useRef(null);

	const selectionRef = useRef({ selection: [] });

	const centroids = useMemo(() => {
		return nodes.filter((node) => node.type === 'centroid');
	}, [nodes]);

	const [forces, setForces] = useState([]);

	const colorScale = d3.interpolateTurbo;

	const centroidScale = useMemo(() => {
		const extent = d3.extent(nodes, (node) => node.count);
		return d3.scaleLinear().domain(extent).range([5, 40]);
	}, [nodes]);

	const { simulation, tick } = useForceSimulation({
		forces,
		data: nodes,
		dimensions,
	});
	useZoom({ svgRef, containerRef });

	useEffect(() => {
		const forces = [
			{
				name: 'center',
				force: d3.forceCenter(dimensions.width / 2, dimensions.height / 2),
			},
			{ name: 'charge', force: d3.forceManyBody().strength(-50) },
			{ name: 'x', force: d3.forceX(dimensions.width / 2).strength(0.05) },
			{ name: 'y', force: d3.forceY(dimensions.height / 2).strength(0.075) },
			{
				name: 'collision',
				force: rectCollision()
					.size(function (d) {
						return d.bbox ? [d.bbox.width + 8, d.bbox.height + 4] : [0, 0];
					})
					.strength(2),
			},
			{
				name: 'links',
				force: d3
					.forceLink(links)
					.id((d) => d.id)
					.strength(2)
					.distance(50),
			},
		];

		setForces(forces);
	}, [links, dimensions, centroidScale]);

	useEffect(() => {
		// TODO: Make this a util
		const handleNodeClick = (node) => {
			const n = pick(node, ['id']);

			if (multiselect) {
				let tempNodes = [];
				const index = selected.findIndex((test) => test.id === n.id);
				if (index >= 0) {
					selected.splice(index, 1);
					tempNodes = selected;
				} else {
					selected.push(n);
					tempNodes = selected;
				}

				onSelect([...tempNodes]);
			} else {
				onSelect([n]);
			}
		};

		selectionRef.current.selection = [...selected];

		// test if two nodes are equivalent
		const nodeTest = (node1, node2) => {
			return node1.id === node2.id;
		};

		// test if the node is in the selection
		const nodeInSelection = (node) => {
			return selectionRef.current.selection.some((n) => nodeTest(node, n));
		};

		// render centroids
		const centroid = d3
			.select(centroidGroupRef.current)
			.selectAll('.centroid')
			.data(centroids, (d) => d.id)
			.join(
				(enter) => {
					const g = enter
						.append('g')
						.classed('centroid', true)
						.on('click', (event, d) => handleNodeClick(d))
						.style('cursor', 'pointer');

					// the border to draw: none, black (selected), blue (hovered)
					g.append('circle')
						.classed('border', true)
						.attr('r', (d) => centroidScale(d.count) + 5)
						.attr('fill', 'transparent')
						.attr('stroke-width', 3)
						.attr('stroke', (d) => (nodeInSelection(d) ? 'black' : 'transparent'))
						.attr('opacity', (d) => (nodeInSelection(d) ? 1.0 : 0));

					// main body of the node
					g.append('circle')
						.classed('body', true)
						.attr('r', (d) => centroidScale(d.count))
						.attr('fill', (d) => colorScale(d.colorIndex / centroids.length))
						.attr('fill-opacity', opacity.fill)
						.attr('stroke', 'transparent');

					// stroke to draw around the body (adding a stroke to the body looked wrong)
					g.append('circle')
						.classed('stroke', true)
						.attr('r', (d) => centroidScale(d.count) + 1)
						.attr('fill', 'transparent')
						.attr('stroke', (d) => colorScale(d.colorIndex / centroids.length))
						.attr('stroke-width', 2)
						.attr('stroke-opacity', opacity.stroke);

					// text to show on hover
					g.append('text')
						.text((d) => d.count)
						.attr('text-anchor', 'middle')
						.attr('dy', '.35em')
						.attr('fill', 'black')
						.attr('fill-opacity', 0)
						.style('font-size', function (d) {
							return (
								Math.min(centroidScale(d.count), ((2 * centroidScale(d.count)) / this.getComputedTextLength()) * 12) +
								'px'
							);
						})
						.style('pointer-events', 'none');
					return g;
				},
				(update) => {
					update.attr('transform', (d) => `translate(${d.x} ${d.y})`);

					update
						.selectAll('circle.body')
						.attr('r', (d) => centroidScale(d.count))
						.attr('fill', (d) => colorScale(d.colorIndex / centroids.length));

					update
						.selectAll('circle.stroke')
						.attr('r', (d) => centroidScale(d.count) + 1)
						.attr('stroke', (d) => colorScale(d.colorIndex / centroids.length));

					update
						.selectAll('circle.border')
						.attr('r', (d) => centroidScale(d.count) + 5)
						.attr('stroke', (d) => (nodeInSelection(d) ? 'black' : 'transparent'));

					return update;
				},
				(exit) => exit.remove()
			);

		const term = d3
			.select(termGroupRef.current)
			.selectAll('text')
			.data(
				nodes.filter((node) => node.type === 'child'),
				(d) => d.parentId + d.label
			)
			.join(
				(enter) =>
					enter
						.append('text')
						.attr('x', (d) => d.x)
						.attr('y', (d) => d.y)
						.attr('dominant-baseline', 'middle')
						.attr('fill', (d) => colorScale(d.colorIndex / centroids.length))
						.text((d) => d.label),
				(update) =>
					update
						.attr('y', function (d) {
							return d.y + d3.select(this).node().getBBox().height / 2;
						})
						.attr('x', (d) => d.x),
				(exit) => exit.remove()
			);

		// record the bounding box for each term
		term.each(function (d) {
			d.bbox = d3.select(this).node().getBBox();
		});

		// set up dragging
		centroid.call(dragHook(simulation));
		term.call(dragHook(simulation));

		// set up hover effects
		centroid.on('mouseover', function (event, d) {
			centroid
				.selectAll('circle.body')
				.transition()
				.attr('fill-opacity', (e) => (e === d ? 0.7 : 0.05));
			centroid
				.selectAll('text')
				.transition()
				.attr('fill-opacity', (e) => (e === d ? 0.7 : 0));
			term.transition().attr('fill-opacity', (e) => (e.parentId === d.id ? 1.0 : 0.2));

			d3.select(this).select('circle.border').transition().attr('stroke', 'blue').attr('opacity', 1);
		});

		centroid.on('mouseout', function () {
			centroid.selectAll('circle.body').transition().attr('fill-opacity', opacity.fill);
			centroid.selectAll('text').transition().attr('fill-opacity', 0);
			term.transition().attr('fill-opacity', 1.0);

			d3.select(this)
				.select('circle.border')
				.transition()
				.attr('stroke', (d) => (nodeInSelection(d) ? 'black' : 'transparent'))
				.attr('opacity', (d) => (nodeInSelection(d) ? 1.0 : 0));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [centroidGroupRef, termGroupRef, data, tick, simulation, selected]);

	return (
		<svg cy-test="typograph-data" width={dimensions.width} height={dimensions.height} ref={svgRef}>
			<g ref={containerRef}>
				<g ref={termGroupRef} />
				<g ref={centroidGroupRef} />
			</g>
		</svg>
	);
};
